import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import {
  Ticket, AlertTriangle, CheckCircle2, Clock, TrendingUp, TrendingDown,
  Loader2, RefreshCw, Award, Target, Zap, BarChart3, MapPin
} from 'lucide-react';

const COLORS = ['#1E3A8A', '#3B82F6', '#06B6D4', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#64748B'];
const priorityColors = { urgente: '#DC2626', alta: '#EA580C', media: '#F59E0B', bassa: '#94A3B8' };
const priorityLabels = { urgente: 'Urgente', alta: 'Alta', media: 'Media', bassa: 'Bassa' };
const statusLabels = {
  aperto: 'Aperto', assegnato: 'Assegnato', in_lavorazione: 'In Lavorazione',
  in_attesa_pezzi: 'In Attesa', completato: 'Completato', chiuso: 'Chiuso'
};
const statusColors = {
  aperto: '#3B82F6', assegnato: '#8B5CF6', in_lavorazione: '#06B6D4',
  in_attesa_pezzi: '#A855F7', completato: '#22C55E', chiuso: '#64748B'
};

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [hotelFilter, setHotelFilter] = useState('all');

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ period });
      if (hotelFilter && hotelFilter !== 'all') params.set('hotel_id', hotelFilter);
      const { data: analyticsData } = await api.get(`/analytics/advanced?${params.toString()}`);
      setData(analyticsData);
    } catch (err) {
      toast.error('Errore nel caricamento analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnalytics(); }, [period, hotelFilter]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  const trendChange = (current, prev) => {
    if (prev === 0) return current > 0 ? '+100%' : '0%';
    const pct = ((current - prev) / prev) * 100;
    return `${pct > 0 ? '+' : ''}${pct.toFixed(0)}%`;
  };

  const comp = data?.period_comparison || {};
  const sla = data?.sla || {};

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Statistiche Avanzate</h1>
          <p className="text-slate-500 mt-1">
            {data?.filter_hotel_id
              ? `Analisi: ${(data.available_hotels.find(h => h.id === data.filter_hotel_id) || {}).name || ''}`
              : 'Analisi performance e KPI · Tutti gli hotel'}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={hotelFilter} onValueChange={setHotelFilter}>
            <SelectTrigger className="w-56" data-testid="hotel-filter-select">
              <SelectValue placeholder="Tutti gli hotel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli hotel</SelectItem>
              {(data?.available_hotels || []).map(h => (
                <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Ultimi 7 giorni</SelectItem>
              <SelectItem value="30">Ultimi 30 giorni</SelectItem>
              <SelectItem value="90">Ultimi 90 giorni</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAnalytics} variant="outline" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {data && (
        <>
          {/* Alert qualità dati — "Altro" oltre soglia */}
          {data.altro_alert?.is_critical && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-4 flex items-start gap-3"
              data-testid="altro-alert-banner"
            >
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-900 dark:text-amber-100">
                  Qualità dei dati: {data.altro_alert.altro_pct}% dei ticket finisce in &quot;Altro&quot; (soglia {data.altro_alert.threshold_pct}%)
                </p>
                <p className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                  Su {data.total_tickets} ticket nel periodo, <b>{data.altro_alert.altro_count}</b> sono stati categorizzati come &quot;Altro&quot;.
                  Ricorda al team di scegliere sempre la categoria specifica più adatta —
                  altrimenti le statistiche perdono affidabilità.
                </p>
              </div>
            </div>
          )}

          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
            <Card className="stat-card" data-testid="kpi-total">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <Ticket className="w-5 h-5 text-blue-400" />
                  <span className={`text-xs font-bold ${comp.created_change_pct > 0 ? 'text-red-500' : 'text-green-500'}`}>
                    {trendChange(comp.current_created, comp.prev_created)}
                  </span>
                </div>
                <p className="text-2xl font-black text-slate-900">{comp.current_created}</p>
                <p className="text-xs text-slate-500 mt-1">Ticket creati</p>
              </CardContent>
            </Card>
            <Card className="stat-card" data-testid="kpi-resolved">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span className={`text-xs font-bold ${comp.resolved_change_pct > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {trendChange(comp.current_resolved, comp.prev_resolved)}
                  </span>
                </div>
                <p className="text-2xl font-black text-green-700">{comp.current_resolved}</p>
                <p className="text-xs text-slate-500 mt-1">Risolti</p>
              </CardContent>
            </Card>
            <Card className="stat-card" data-testid="kpi-avg-per-day">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <TrendingUp className="w-5 h-5 text-indigo-400" />
                </div>
                <p className="text-2xl font-black text-indigo-700">{(data.avg_tickets_per_day ?? 0).toFixed(2)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Media ticket/giorno
                  {hotelFilter !== 'all' ? ' · hotel' : ' · gruppo'}
                </p>
              </CardContent>
            </Card>
            <Card className="stat-card" data-testid="kpi-avg-per-tech">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <Zap className="w-5 h-5 text-purple-400" />
                </div>
                <p className="text-2xl font-black text-purple-700">{(data.avg_tickets_per_technician ?? 0).toFixed(2)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Media ticket/tecnico
                  {data.assigned_technicians_count > 0 && (
                    <span className="block text-[10px] text-slate-400">
                      su {data.assigned_technicians_count} tecnici
                      {hotelFilter !== 'all' ? ' assegnati' : ''}
                    </span>
                  )}
                </p>
              </CardContent>
            </Card>
            <Card className="stat-card" data-testid="kpi-sla-24h">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <Target className="w-5 h-5 text-cyan-400" />
                </div>
                <p className="text-2xl font-black text-cyan-700">{sla.pct_24h}%</p>
                <p className="text-xs text-slate-500 mt-1">SLA 24h</p>
              </CardContent>
            </Card>
            <Card className="stat-card" data-testid="kpi-sla-48h">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <Clock className="w-5 h-5 text-violet-400" />
                </div>
                <p className="text-2xl font-black text-violet-700">{sla.pct_48h}%</p>
                <p className="text-xs text-slate-500 mt-1">SLA 48h</p>
              </CardContent>
            </Card>
            <Card className="stat-card" data-testid="kpi-backlog">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <p className="text-2xl font-black text-amber-600">
                  {(data.status_breakdown?.aperto || 0) + (data.status_breakdown?.assegnato || 0)}
                </p>
                <p className="text-xs text-slate-500 mt-1">Backlog</p>
              </CardContent>
            </Card>
          </div>

          {/* Trend + Status breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="border-slate-200 lg:col-span-2" data-testid="ticket-trend-chart">
              <CardHeader>
                <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-600" />
                  Trend Ticket ({period}gg)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.ticket_trend}>
                      <defs>
                        <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.15}/>
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorClosed" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22C55E" stopOpacity={0.15}/>
                          <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94A3B8' }} tickFormatter={v => v.slice(5)} />
                      <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} allowDecimals={false} />
                      <Tooltip labelFormatter={v => `Data: ${v}`} />
                      <Area type="monotone" dataKey="created" stroke="#3B82F6" fill="url(#colorCreated)" strokeWidth={2} name="Creati" />
                      <Area type="monotone" dataKey="closed" stroke="#22C55E" fill="url(#colorClosed)" strokeWidth={2} name="Chiusi" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-3 text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-500" />Creati</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500" />Chiusi</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="status-pie-chart">
              <CardHeader>
                <CardTitle className="text-base font-bold text-slate-900">Stato Ticket</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={Object.entries(data.status_breakdown || {}).map(([k, v]) => ({
                          name: statusLabels[k] || k, value: v, color: statusColors[k] || '#64748B'
                        }))}
                        cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value"
                      >
                        {Object.entries(data.status_breakdown || {}).map(([k], i) => (
                          <Cell key={`status-${k}`} fill={statusColors[k] || '#64748B'} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-2 justify-center mt-2">
                  {Object.entries(data.status_breakdown || {}).map(([k, v]) => (
                    <span key={k} className="flex items-center gap-1 text-[10px] text-slate-600">
                      <span className="w-2 h-2 rounded-full" style={{backgroundColor: statusColors[k]}} />
                      {statusLabels[k]}: {v}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Category (bar) + Category % (pie) + Priority Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="border-slate-200" data-testid="category-chart">
              <CardHeader>
                <CardTitle className="text-base font-bold text-slate-900">Distribuzione per Categoria</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.category_distribution} layout="vertical" margin={{ left: 20, right: 16, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-slate-700" opacity={0.4} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: 'currentColor' }} className="text-slate-500 dark:text-slate-300" allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="category"
                        width={110}
                        interval={0}
                        tick={{ fontSize: 12, fill: 'currentColor' }}
                        className="text-slate-600 dark:text-slate-200"
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: 8,
                          color: 'hsl(var(--foreground))',
                          fontSize: 12,
                        }}
                        labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}
                        itemStyle={{ color: 'hsl(var(--muted-foreground))' }}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Ticket">
                        {data.category_distribution.map((entry, i) => (
                          <Cell key={`cat-${entry.category || i}`} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* NEW: Category % pie */}
            <Card className="border-slate-200" data-testid="category-percent-chart">
              <CardHeader>
                <CardTitle className="text-base font-bold text-slate-900">% Ticket per Categoria</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const cats = data.category_distribution || [];
                  const total = cats.reduce((s, c) => s + (c.count || 0), 0);
                  // Group tail (<3%) into "Altre" for readability
                  const threshold = total * 0.03;
                  const main = [];
                  let tail = 0;
                  cats.forEach(c => {
                    if ((c.count || 0) >= threshold) main.push(c);
                    else tail += (c.count || 0);
                  });
                  if (tail > 0) main.push({ category: 'Altre', count: tail });
                  return (
                    <>
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={main}
                              cx="50%" cy="50%"
                              outerRadius={80}
                              innerRadius={38}
                              dataKey="count"
                              nameKey="category"
                              label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                              labelLine={false}
                            >
                              {main.map((entry, i) => (
                                <Cell key={`catpc-${entry.category}-${i}`} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value, name) => {
                                const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                                return [`${value} (${pct}%)`, name];
                              }}
                              contentStyle={{
                                background: 'hsl(var(--card))',
                                border: '1px solid hsl(var(--border))',
                                borderRadius: 8,
                                color: 'hsl(var(--foreground))',
                                fontSize: 12,
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] max-h-20 overflow-y-auto" data-testid="category-percent-legend">
                        {main.map((c, i) => {
                          const pct = total > 0 ? ((c.count / total) * 100).toFixed(1) : '0.0';
                          return (
                            <span key={`legend-${c.category}-${i}`} className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300 min-w-0">
                              <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                              <span className="truncate">{c.category}</span>
                              <span className="font-mono font-semibold text-slate-900 dark:text-slate-100 ml-auto shrink-0">{pct}%</span>
                            </span>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="priority-chart">
              <CardHeader>
                <CardTitle className="text-base font-bold text-slate-900">Distribuzione per Priorita</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={Object.entries(data.priority_distribution || {}).map(([k, v]) => ({
                          name: priorityLabels[k] || k, value: v
                        }))}
                        cx="50%" cy="50%" outerRadius={75} dataKey="value" label={({name, percent}) => `${name} ${(percent*100).toFixed(0)}%`}
                      >
                        {Object.entries(data.priority_distribution || {}).map(([k], i) => (
                          <Cell key={`prio-${k}`} fill={priorityColors[k] || COLORS[i]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-3">
                  {Object.entries(data.priority_distribution || {}).map(([k, v]) => (
                    <span key={k} className="flex items-center gap-1.5 text-xs text-slate-600">
                      <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: priorityColors[k]}} />
                      {priorityLabels[k]}: {v}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Resolution by hotel + SLA */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200" data-testid="resolution-hotel-chart">
              <CardHeader>
                <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  Tempo Medio Risoluzione per Hotel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.resolution_by_hotel}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="hotel" tick={{ fontSize: 10, fill: '#64748B' }} angle={-15} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} label={{ value: 'Ore', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#94A3B8' } }} />
                      <Tooltip formatter={(v) => [`${v}h`, 'Tempo medio']} />
                      <Bar dataKey="avg_hours" fill="#06B6D4" radius={[4, 4, 0, 0]} name="Ore medie" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="sla-card">
              <CardHeader>
                <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Target className="w-4 h-4 text-green-600" />
                  Conformita SLA
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6 pt-2">
                  {/* SLA 24h */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-medium text-slate-700">Risolti entro 24h</span>
                      <span className="font-bold text-slate-900">{sla.within_24h}/{sla.total_resolved} ({sla.pct_24h}%)</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-4">
                      <div className={`h-4 rounded-full transition-all ${sla.pct_24h >= 80 ? 'bg-green-500' : sla.pct_24h >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(sla.pct_24h, 100)}%` }} />
                    </div>
                  </div>
                  {/* SLA 48h */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-medium text-slate-700">Risolti entro 48h</span>
                      <span className="font-bold text-slate-900">{sla.within_48h}/{sla.total_resolved} ({sla.pct_48h}%)</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-4">
                      <div className={`h-4 rounded-full transition-all ${sla.pct_48h >= 80 ? 'bg-green-500' : sla.pct_48h >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(sla.pct_48h, 100)}%` }} />
                    </div>
                  </div>
                  {/* Period comparison */}
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-sm font-bold text-slate-700 mb-3">Confronto Periodo</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-slate-500">Creati</p>
                        <p className="text-lg font-black text-slate-900">{comp.current_created}</p>
                        <p className={`text-xs font-bold ${comp.created_change_pct > 0 ? 'text-red-500' : 'text-green-500'}`}>
                          vs {comp.prev_created} ({comp.created_change_pct > 0 ? '+' : ''}{comp.created_change_pct}%)
                        </p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-slate-500">Risolti</p>
                        <p className="text-lg font-black text-green-700">{comp.current_resolved}</p>
                        <p className={`text-xs font-bold ${comp.resolved_change_pct > 0 ? 'text-green-500' : 'text-red-500'}`}>
                          vs {comp.prev_resolved} ({comp.resolved_change_pct > 0 ? '+' : ''}{comp.resolved_change_pct}%)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Areas + Technician Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200" data-testid="top-areas-card">
              <CardHeader>
                <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-red-500" />
                  Top 10 Aree Problematiche
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[320px] pr-3">
                  <div className="space-y-2">
                    {data.top_problematic_areas?.map((area, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`top-area-${idx}`}>
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">{idx+1}</span>
                          <div>
                            <p className="text-sm font-bold text-slate-900">{area.area}</p>
                            <p className="text-xs text-slate-500">{area.hotel}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {area.urgent > 0 && <Badge className="bg-red-100 text-red-700 text-xs">{area.urgent} urg</Badge>}
                          <Badge variant="outline" className="text-xs">{area.total} ticket</Badge>
                        </div>
                      </div>
                    ))}
                    {(!data.top_problematic_areas || data.top_problematic_areas.length === 0) && (
                      <p className="text-center text-slate-500 py-6">Nessun dato disponibile</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="tech-performance-card">
              <CardHeader>
                <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-500" />
                  Performance Tecnici
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[320px] pr-3">
                  <div className="space-y-2">
                    {data.technician_performance?.map((tech, idx) => (
                      <button
                        key={tech.id || idx}
                        type="button"
                        onClick={() => tech.id && navigate(`/tickets?assigned_to=${tech.id}`)}
                        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-blue-50 hover:ring-1 hover:ring-blue-300 transition-all rounded-lg text-left"
                        data-testid={`tech-perf-${idx}`}
                        title={tech.id ? 'Apri i ticket di questo tecnico' : ''}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            idx === 0 ? 'bg-amber-100 text-amber-700' :
                            idx === 1 ? 'bg-slate-200 text-slate-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>{idx+1}</div>
                          <span className="font-medium text-sm text-slate-900">{tech.name}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-green-700">{tech.completed} risolti</p>
                          <p className="text-xs text-slate-500">
                            ~{tech.avg_resolution_hours < 24 
                              ? `${tech.avg_resolution_hours}h` 
                              : `${Math.round(tech.avg_resolution_hours / 24)}g`} media
                          </p>
                        </div>
                      </button>
                    ))}
                    {(!data.technician_performance || data.technician_performance.length === 0) && (
                      <p className="text-center text-slate-500 py-6">Nessun dato disponibile</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
